import { useState, useEffect } from 'react';

/**
 * Custom hook to calculate smooth hero background zoom and parallax offset on scroll.
 * 
 * @param {Object} options
 * @param {number} [options.baseScale=1.1] - Initial scale when scrollY = 0
 * @param {number} [options.maxScale=1.45] - Maximum scale cap
 * @param {number} [options.zoomSpeed=0.0006] - Speed of zoom effect relative to scroll
 * @param {number} [options.parallaxSpeed=0.25] - Speed of parallax translation relative to scroll
 * @returns {{ scale: number, translateY: number, opacity: number }}
 */
export function useScrollZoom(options = {}) {
  const {
    baseScale = 1.1,
    maxScale = 1.45,
    zoomSpeed = 0.0006,
    parallaxSpeed = 0.25,
  } = options;

  const [scrollState, setScrollState] = useState({
    scale: baseScale,
    translateY: 0,
    opacity: 1,
  });

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY || window.pageYOffset;
          const calculatedScale = Math.min(baseScale + scrollY * zoomSpeed, maxScale);
          const calculatedTranslate = scrollY * parallaxSpeed;
          // Fade image slightly as user scrolls deep past the hero (over 800px)
          const calculatedOpacity = Math.max(1 - scrollY / 1200, 0.4);

          setScrollState({
            scale: calculatedScale,
            translateY: calculatedTranslate,
            opacity: calculatedOpacity,
          });
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Trigger initial calculation
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [baseScale, maxScale, zoomSpeed, parallaxSpeed]);

  return scrollState;
}
